import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { fetchWrapper } from "@helpers/fetchwrapper";

import Search from "./Search";

import { filterNames } from "@helpers/data/filterNames";

import styles from "./styles/map.module.css";

const YANDEX_MAPS_API_KEY = "7e9247c8-4c61-409a-91c5-c49872daf93e";
const MAP_CENTER = [55.958736, 54.735152];
const MAP_ZOOM = 7;
const CLUSTER_GRID_SIZE = 64;
const MARKER_SOURCE = "college-map-source";

let ymapsLoadPromise = null;
let clustererCdnRegistered = false;

function loadYmaps3(apiKey) {
  if (window.ymaps3) {
    return window.ymaps3.ready.then(() => window.ymaps3);
  }

  if (!ymapsLoadPromise) {
    ymapsLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`;
      script.async = true;
      script.onload = () => {
        window.ymaps3.ready.then(() => resolve(window.ymaps3));
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  return ymapsLoadPromise;
}

async function loadClusterer(ymaps3) {
  if (!clustererCdnRegistered) {
    ymaps3.import.registerCdn("https://cdn.jsdelivr.net/npm/{package}", [
      "@yandex/ymaps3-clusterer@0.0",
    ]);
    clustererCdnRegistered = true;
  }

  return ymaps3.import("@yandex/ymaps3-clusterer");
}

function flattenToFeatures(coordinates) {
  const features = [];
  let id = 0;

  coordinates.forEach((group) => {
    group.Coordinates?.forEach((point) => {
      if (point.GeoLon == null || point.GeoLat == null) {
        return;
      }

      features.push({
        type: "Feature",
        id,
        geometry: {
          coordinates: [point.GeoLon, point.GeoLat],
        },
        properties: {
          point,
          idType: group.IdType,
          companiesCount: point.Companies?.length || 1,
        },
      });
      id += 1;
    });
  });

  return features;
}

function getClusterCompaniesCount(features) {
  return features.reduce(
    (sum, feature) => sum + (feature.properties.companiesCount || 1),
    0
  );
}

function getBoundsFromCoordinates(coordinatesList) {
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  coordinatesList.forEach(([lon, lat]) => {
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ];
}

function createMarkerElement(count, markerClass, countClass) {
  const el = document.createElement("div");
  el.className = markerClass;

  if (count > 1) {
    const countEl = document.createElement("span");
    countEl.className = countClass;
    countEl.textContent = String(count);
    el.appendChild(countEl);
  }

  return el;
}

function createClusterElement(count, clusterClass, countClass) {
  const el = document.createElement("div");
  el.className = clusterClass;

  const countEl = document.createElement("span");
  countEl.className = countClass;
  countEl.textContent = String(count);
  el.appendChild(countEl);

  return el;
}

function getCompanyPath(comp) {
  return `/company/${comp.Slug}`;
}

function Map({ data }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const clustererRef = useRef(null);
  const mapApiRef = useRef(null);
  const onSelectPointRef = useRef(null);

  const [load, setLoad] = useState(false);
  const [filter, setFilter] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState({
    filter: data.Filters || [],
    data: data.Data || [],
    total: data.Total,
    totals: data.Totals || [],
    coordinats: data.Coordinats || [],
  });
  const [mapCoordinates, setMapCoordinates] = useState([]);
  const [isMapFiltered, setIsMapFiltered] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  onSelectPointRef.current = setSelectedPoint;

  const buildFilterParams = useCallback((activeFilters) => {
    const requestFilters = {};

    for (let i = 0; i < filterNames.length; i++) {
      const name = filterNames[i];
      requestFilters[name] = [];
      activeFilters.forEach((f) => {
        if (f.name === name) {
          requestFilters[name].push(f.value);
        }
      });
    }

    const params = {};
    for (let i = 0; i < filterNames.length; i++) {
      const name = filterNames[i];
      if (requestFilters[name]?.length > 0) {
        params[name] = requestFilters[name];
      }
    }

    return params;
  }, []);

  const loadMapCompanies = useCallback(
    (query = "", activeFilters = []) => {
      const hasFilters =
        Boolean(query?.trim()) || activeFilters.length > 0;

      const requestData = {
        text: query || "",
        limit: hasFilters ? 200 : 500,
        offset: 0,
        CoordinateSize: 3,
        ...buildFilterParams(activeFilters),
      };

      fetchWrapper
        .post(
          `/v1/api/app/findCompaniesWithPracticesAndMentorships`,
          requestData
        )
        .then((res) => {
          setMapCoordinates(res.Coordinats || []);
        });
    },
    [buildFilterParams]
  );

  useEffect(() => {
    loadMapCompanies();
  }, [loadMapCompanies]);

  const search = useCallback(
    (query, clearFilters = false) => {
      setLoad(true);
      let effectiveQuery = query;

      if (query !== undefined) {
        setSearchQuery(query);
      } else {
        effectiveQuery = searchQuery;
      }

      const activeFilters = clearFilters ? [] : filter;
      const hasActiveSearch =
        !clearFilters &&
        (Boolean(effectiveQuery?.trim()) || activeFilters.length > 0);

      setIsMapFiltered(hasActiveSearch);

      const requestData = {
        text: effectiveQuery || "",
        limit: 2,
        offset: 0,
        ...buildFilterParams(activeFilters),
      };

      fetchWrapper
        .post(`/v1/api/app/findCompaniesWithPracticesAndMentorships`, requestData)
        .then((res) => {
          setSearchResult({
            data: res.Data,
            filter: res.Filters,
            total: res.Total,
            totals: res.Totals,
            coordinats: res.Coordinats,
          });

          setLoad(false);
        });

      if (hasActiveSearch) {
        loadMapCompanies(effectiveQuery, activeFilters);
      } else {
        loadMapCompanies();
      }
    },
    [searchQuery, filter, buildFilterParams, loadMapCompanies]
  );

  useEffect(() => {
    if (!mapContainerRef.current) {
      return undefined;
    }

    let cancelled = false;

    const initMap = async () => {
      try {
        const ymaps3 = await loadYmaps3(YANDEX_MAPS_API_KEY);
        const { YMapClusterer, clusterByGrid } = await loadClusterer(ymaps3);

        if (cancelled || !mapContainerRef.current) {
          return;
        }

        const {
          YMap,
          YMapDefaultSchemeLayer,
          YMapFeatureDataSource,
          YMapLayer,
          YMapMarker,
        } = ymaps3;

        if (mapInstanceRef.current) {
          mapInstanceRef.current.destroy();
          mapInstanceRef.current = null;
          clustererRef.current = null;
        }

        const map = new YMap(mapContainerRef.current, {
          location: {
            center: MAP_CENTER,
            zoom: MAP_ZOOM,
          },
        });

        map
          .addChild(new YMapDefaultSchemeLayer())
          .addChild(new YMapFeatureDataSource({ id: MARKER_SOURCE }))
          .addChild(
            new YMapLayer({
              source: MARKER_SOURCE,
              type: "markers",
              zIndex: 1800,
            })
          );

        mapInstanceRef.current = map;
        mapApiRef.current = {
          YMapClusterer,
          clusterByGrid,
          YMapMarker,
        };
        setMapReady(true);
      } catch (error) {
        console.error("College map init error:", error);
        setMapReady(false);
      }
    };

    initMap();

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
        clustererRef.current = null;
        mapApiRef.current = null;
      }
    };
  }, []);

  const activeCoordinates = mapCoordinates;

  useEffect(() => {
    setSelectedPoint(null);
  }, [isMapFiltered, activeCoordinates]);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const mapApi = mapApiRef.current;
    const coordinates = activeCoordinates;

    if (!mapReady || !map || !mapApi) {
      return undefined;
    }

    if (!coordinates?.length) {
      if (clustererRef.current) {
        map.removeChild(clustererRef.current);
        clustererRef.current = null;
      }
      return undefined;
    }

    const features = flattenToFeatures(coordinates);
    if (!features.length) {
      return undefined;
    }

    const { YMapClusterer, clusterByGrid, YMapMarker } = mapApi;
    const markerClass = styles.marker;
    const markerCountClass = styles.markerCount;
    const clusterClass = styles.cluster;
    const clusterCountClass = styles.clusterCount;

    if (clustererRef.current) {
      map.removeChild(clustererRef.current);
      clustererRef.current = null;
    }

    const marker = (feature) => {
      const { point, idType, companiesCount } = feature.properties;
      const markerColorClass =
        idType === 3 ? styles.college : styles.university;
      const markerEl = createMarkerElement(
        companiesCount,
        `${markerClass} ${markerColorClass}`,
        markerCountClass
      );
      markerEl.title =
        point.Companies?.map((company) => company.Name).join(", ") || "";

      markerEl.addEventListener("click", (e) => {
        e.stopPropagation();
        onSelectPointRef.current?.(point);
      });

      return new YMapMarker(
        {
          coordinates: feature.geometry.coordinates,
          source: MARKER_SOURCE,
        },
        markerEl
      );
    };

    const cluster = (coords, clusterFeatures) =>
      new YMapMarker(
        {
          coordinates: coords,
          source: MARKER_SOURCE,
          onClick() {
            const bounds = getBoundsFromCoordinates(
              clusterFeatures.map((feature) => feature.geometry.coordinates)
            );
            map.update({
              location: {
                bounds,
                duration: 300,
              },
            });
          },
        },
        createClusterElement(
          getClusterCompaniesCount(clusterFeatures),
          clusterClass,
          clusterCountClass 
        )
      );

    const clusterer = new YMapClusterer({
      method: clusterByGrid({ gridSize: CLUSTER_GRID_SIZE }),
      features,
      marker,
      cluster,
    });

    map.addChild(clusterer);
    clustererRef.current = clusterer;

    return () => {
      if (clustererRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeChild(clustererRef.current);
        clustererRef.current = null;
      }
    };
  }, [mapReady, activeCoordinates, isMapFiltered]);

  return (
    <div className={`${styles.block} container`}>
      <div className={styles.header}>Компании республики Башкортостан</div>
      <Search
        load={load}
        query={searchQuery}
        search={search}
        filterUser={filter}
        setFilterUser={setFilter}
        results={searchResult}
        setResults={setSearchResult}
      />
      <div className={styles.secondHeader}>
        Карта компаний
        <br />
        Республики Башкортостан
      </div>
      <div
        className={styles.mapWrapper}
        onClick={() => setSelectedPoint(null)}
      >
        <div ref={mapContainerRef} className={styles.map} />
        {selectedPoint && mapReady ? (
          <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className={styles.popupClose}
              onClick={() => setSelectedPoint(null)}
              aria-label="Закрыть"
            >
              ×
            </button>
            {selectedPoint.Companies?.map((comp) => (
              <Link
                key={comp.Id}
                to={getCompanyPath(comp)}
                className={styles.popupItem}
                target="_blank"
              >
                <div className={styles.popupTitle}>{comp.Name}</div>
                {comp.Address ? (
                  <div className={styles.popupAddress}>{comp.Address}</div>
                ) : null}
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default Map;
